import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { useNavigate } from "react-router-dom";
import { CEIScoreDisplay } from "../components/cei/CEIScoreDisplay";
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Target, 
  Clock, 
  BarChart3,
  ArrowRight,
  Calendar,
  Building2,
  CheckCircle,
  XCircle,
  Activity,
  Zap,
  ChevronRight,
  Shield,
  Radio
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Execution stages for the flow visualization
const PERFORMANCE_STAGES = [
  { key: 'closed', label: 'CLOSED', color: '#10B981' },
  { key: 'profitable', label: 'PROFITABLE', color: '#D4AF37' },
  { key: 'break_even', label: 'BREAK EVEN', color: '#F59E0B' },
  { key: 'loss', label: 'LOSS', color: '#EF4444' },
];

export default function PerformanceDashboardPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [systemStatus, setSystemStatus] = useState('stable');
  const canvasRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/analytics/closed-deals`,
        { withCredentials: true }
      );
      setAnalytics(response.data);
      
      // Determine system status
      const summary = response.data?.summary || {};
      if (summary.close_rate >= 70) setSystemStatus('stable');
      else if (summary.close_rate >= 40) setSystemStatus('alert');
      else setSystemStatus('bottleneck');
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "$0";
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  const getStrategyLabel = (strategy) => {
    const labels = {
      cash: "Cash",
      wholesale: "Wholesale",
      fix_flip: "Fix & Flip",
      subject_to: "Subject-To",
      seller_finance: "Seller Finance",
      wrap: "Wrap",
      unknown: "Unknown"
    };
    return labels[strategy] || strategy;
  };

  // Canvas animation for profit flow
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analytics) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    let particles = [];
    let animationId;

    const createParticle = () => {
      const stageIdx = Math.floor(Math.random() * (PERFORMANCE_STAGES.length - 1));
      return {
        x: (stageIdx + 0.5) * (canvas.width / PERFORMANCE_STAGES.length),
        y: canvas.height / 2 + (Math.random() - 0.5) * 40,
        targetX: (stageIdx + 1.5) * (canvas.width / PERFORMANCE_STAGES.length),
        progress: 0,
        speed: 0.005 + Math.random() * 0.005,
        color: PERFORMANCE_STAGES[stageIdx].color
      };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw connection lines
      PERFORMANCE_STAGES.forEach((stage, idx) => {
        if (idx < PERFORMANCE_STAGES.length - 1) {
          const x1 = (idx + 0.5) * (canvas.width / PERFORMANCE_STAGES.length);
          const x2 = (idx + 1.5) * (canvas.width / PERFORMANCE_STAGES.length);
          ctx.beginPath();
          ctx.moveTo(x1, canvas.height / 2);
          ctx.lineTo(x2, canvas.height / 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.03)';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });

      // Manage particles
      if (Math.random() < 0.03 && particles.length < 15) {
        particles.push(createParticle());
      }

      particles = particles.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;
        
        const x = p.x + (p.targetX - p.x) * p.progress;
        const y = p.y + Math.sin(p.progress * Math.PI) * 10;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.5 * (1 - p.progress);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [analytics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const byStrategy = analytics?.by_strategy || {};
  const byMonth = analytics?.by_month || {};
  const recentClosed = analytics?.recent_closed || [];

  // Calculate performance breakdown
  const profitableDeals = recentClosed.filter(d => (d.closed_metrics?.actual_profit || 0) > 0).length;
  const breakEvenDeals = recentClosed.filter(d => (d.closed_metrics?.actual_profit || 0) === 0).length;
  const lossDeals = recentClosed.filter(d => (d.closed_metrics?.actual_profit || 0) < 0).length;

  const performanceMetrics = [
    { ...PERFORMANCE_STAGES[0], count: summary.total_closed || 0, capital: summary.total_revenue || 0 },
    { ...PERFORMANCE_STAGES[1], count: profitableDeals, capital: summary.total_profit > 0 ? summary.total_profit : 0 },
    { ...PERFORMANCE_STAGES[2], count: breakEvenDeals, capital: 0 },
    { ...PERFORMANCE_STAGES[3], count: lossDeals, capital: summary.total_profit < 0 ? Math.abs(summary.total_profit) : 0 },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass} data-testid="performance-dashboard">
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-[#0A0A0A]/80 backdrop-blur-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Left: Branding + Status */}
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-lg font-semibold tracking-tight">
                    Performance <span className="text-[#D4AF37]">Intelligence</span>
                  </h1>
                  <p className="text-[9px] text-white/50 uppercase tracking-[0.15em]">Capital Performance Analytics</p>
                </div>
                
                {/* Live + System Status */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                    <div className="relative w-2 h-2">
                      <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                      <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                    </div>
                    <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                  </div>
                  
                  <SystemStatusBadge status={systemStatus} />
                </div>
              </div>

              {/* Center: Key Metrics */}
              <div className="flex items-center gap-8">
                {/* CEI Score - Primary Position */}
                <CEIScoreDisplay variant="header" className="" />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Total Profit" 
                  value={formatCurrency(summary.total_profit)}
                  trend={summary.total_profit > 0 ? "+12.4%" : "-5.2%"}
                  positive={summary.total_profit > 0}
                  onClick={() => navigate('/deals?stage=closed')}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Avg ROI" 
                  value={`${summary.avg_roi || 0}%`}
                  trend={summary.avg_roi >= 15 ? "+2.1" : "-1.5"}
                  positive={summary.avg_roi >= 15}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Closed" 
                  value={summary.total_closed || 0}
                  highlight
                />
              </div>

              {/* Right: Time */}
              <div className="text-right">
                <div className="font-mono text-lg text-white/70 tracking-tight">
                  {currentTime.toLocaleTimeString('en-US', { hour12: false })}
                </div>
                <div className="text-[9px] text-white/50 uppercase tracking-wider">Eastern</div>
              </div>
            </div>
          </div>
        </header>

        {/* Performance Flow Visualization */}
        <section className="border-b border-white/[0.06] bg-black/80 relative">
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
          <div className="px-6 py-6 relative z-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Performance Flow</h2>
              <span className="text-[10px] text-white/50 font-mono">
                Total Revenue: {formatCurrency(summary.total_revenue)}
              </span>
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              {performanceMetrics.map((stage, idx) => (
                <PerformanceStageCard key={stage.key} stage={stage} index={idx} />
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="p-6">
          <div className="grid grid-cols-12 gap-5">
            {/* Left: KPI Cards + Quick Stats */}
            <div className="col-span-4 space-y-5">
              {/* Revenue Summary */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Revenue Summary</h3>
                  <DollarSign className="w-4 h-4 text-[#D4AF37]" />
                </div>
                <div className="space-y-4">
                  <MetricRow 
                    label="Total Revenue" 
                    value={formatCurrency(summary.total_revenue)} 
                    trend="+14.2%" 
                    positive 
                  />
                  <MetricRow 
                    label="Total Costs" 
                    value={formatCurrency(summary.total_costs)} 
                    trend="-8.5%" 
                    positive 
                  />
                  <MetricRow 
                    label="Net Profit" 
                    value={formatCurrency(summary.total_profit)} 
                    trend={summary.total_profit > 0 ? "+22.1%" : "-15.3%"} 
                    positive={summary.total_profit > 0} 
                    highlight 
                  />
                  <MetricRow 
                    label="Avg Profit/Deal" 
                    value={formatCurrency(summary.avg_profit_per_deal)} 
                    trend="+5.8%" 
                    positive 
                  />
                </div>
              </div>

              {/* Win/Loss Summary */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Execution Summary</h3>
                  <Activity className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-500/[0.05] border border-emerald-500/20 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                      <span className="text-[10px] text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Won</span>
                    </div>
                    <p className="text-2xl font-mono font-light text-white">{summary.total_closed || 0}</p>
                    <p className="text-[10px] text-white/50 mt-1">Closed deals</p>
                  </div>
                  <div className="bg-red-500/[0.05] border border-red-500/20 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 text-[#FF3B30]" />
                      <span className="text-[10px] text-[#FF3B30] uppercase tracking-wider">Dead</span>
                    </div>
                    <p className="text-2xl font-mono font-light text-white">{summary.total_dead || 0}</p>
                    <p className="text-[10px] text-white/50 mt-1">Lost deals</p>
                  </div>
                </div>
              </div>

              {/* System Status */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">System Status</h3>
                  <Radio className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                </div>
                <div className="space-y-2">
                  <StatusRow label="Analytics Engine" status="operational" />
                  <StatusRow label="Data Sync" status="operational" />
                  <StatusRow label="Performance AI" status="operational" />
                </div>
              </div>
            </div>

            {/* Right: Strategy & Monthly Performance */}
            <div className="col-span-8 space-y-5">
              {/* Strategy Performance */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-sm font-semibold text-white">Strategy Performance</h3>
                    <p className="text-[10px] text-white/50 mt-0.5">Profit breakdown by execution strategy</p>
                  </div>
                  <BarChart3 className="w-5 h-5 text-[#D4AF37]" />
                </div>

                {Object.keys(byStrategy).length === 0 ? (
                  <div className="text-center py-12 border border-white/[0.06] rounded-lg">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 text-white/30" />
                    <p className="text-white/50">No closed deals yet</p>
                    <p className="text-[10px] text-white/40 mt-1">Close your first deal to see strategy analytics</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(byStrategy).map(([strategy, data]) => {
                      const percentage = summary.total_closed > 0 
                        ? Math.round((data.count / summary.total_closed) * 100) 
                        : 0;
                      return (
                        <StrategyRow 
                          key={strategy}
                          strategy={getStrategyLabel(strategy)}
                          count={data.count}
                          profit={data.profit}
                          percentage={percentage}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Monthly Performance */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-5">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-sm font-semibold text-white">Monthly Performance</h3>
                    <p className="text-[10px] text-white/50 mt-0.5">Trailing 12-month capital deployment</p>
                  </div>
                  <Calendar className="w-5 h-5 text-[#D4AF37]" />
                </div>

                {Object.keys(byMonth).length === 0 ? (
                  <div className="text-center py-12 border border-white/[0.06] rounded-lg">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-white/30" />
                    <p className="text-white/50">No monthly data available</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3 max-h-[280px] overflow-y-auto pr-2">
                    {Object.entries(byMonth).reverse().slice(0, 12).map(([month, data]) => (
                      <MonthCard key={month} month={month} data={data} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Closed Deals Table */}
          <div className="mt-5 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <div>
                <h3 className="text-sm font-semibold text-white">Recent Closed Transactions</h3>
                <p className="text-[10px] text-white/50 mt-0.5">Latest capital exits with P&L metrics</p>
              </div>
              <button 
                onClick={() => navigate('/deals?stage=closed')}
                className="flex items-center gap-2 text-[10px] text-[#D4AF37] hover:text-[#F2D06B] transition-all duration-300 uppercase tracking-wider font-semibold"
              >
                View All <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            {recentClosed.length === 0 ? (
              <div className="text-center py-16 px-6">
                <Building2 className="w-16 h-16 text-white/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Closed Transactions</h3>
                <p className="text-sm text-white/50 mb-6">Close your first deal to start tracking performance</p>
                <button 
                  onClick={() => navigate('/deals')}
                  className="px-6 py-2.5 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-semibold rounded-sm hover:opacity-90 transition-opacity"
                >
                  View Active Transactions
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-left px-5 py-3">Property</th>
                      <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-left px-5 py-3">Strategy</th>
                      <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-right px-5 py-3">Purchase</th>
                      <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-right px-5 py-3">Sale</th>
                      <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-right px-5 py-3">Profit</th>
                      <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-right px-5 py-3">ROI</th>
                      <th className="text-[10px] text-white/40 uppercase tracking-wider font-normal text-right px-5 py-3">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentClosed.map((deal) => {
                      const metrics = deal.closed_metrics || {};
                      return (
                        <tr 
                          key={deal.deal_id} 
                          onClick={() => navigate(`/deals/${deal.deal_id}`)}
                          className="border-b border-white/[0.02] hover:bg-white/[0.02] transition-all duration-300 cursor-pointer group"
                        >
                          <td className="px-5 py-4">
                            <span className="text-sm text-white group-hover:text-[#D4AF37] transition-all duration-300">
                              {deal.address || "Unknown Address"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className="px-2 py-0.5 bg-white/[0.04] border border-white/[0.06] rounded text-[10px] font-medium text-white/70 uppercase tracking-wider">
                              {getStrategyLabel(deal.strategy)}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-right font-mono text-sm text-white/70">
                            {formatCurrency(metrics.actual_purchase_price)}
                          </td>
                          <td className="px-5 py-4 text-right font-mono text-sm text-white/70">
                            {formatCurrency(metrics.actual_sale_price)}
                          </td>
                          <td className={`px-5 py-4 text-right font-mono text-sm font-medium ${
                            metrics.actual_profit >= 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'
                          }`}>
                            {formatCurrency(metrics.actual_profit)}
                          </td>
                          <td className="px-5 py-4 text-right font-mono text-sm text-white/70">
                            {metrics.roi || 0}%
                          </td>
                          <td className="px-5 py-4 text-right font-mono text-sm text-white/70">
                            {metrics.days_on_market || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// Components

function SystemStatusBadge({ status }) {
  const config = {
    stable: { label: 'Performance Optimal', color: 'emerald' },
    alert: { label: 'Performance Alert', color: 'amber' },
    bottleneck: { label: 'Review Required', color: 'red' }
  };
  const { label, color } = config[status];
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 bg-${color}-500/[0.08] border border-${color}-500/20 rounded`}>
      <Shield className={`w-3 h-3 text-${color}-400`} />
      <span className={`text-[10px] text-${color}-400 uppercase tracking-wider`}>{label}</span>
    </div>
  );
}

function GlobalMetric({ label, value, trend, positive, highlight, onClick }) {
  // Hide trend indicators when value is $0 or zero
  const isZeroValue = value === "$0" || value === "$0.00" || value === "0" || value === "$0K" || value === "$0.00M";
  const showTrend = trend && !isZeroValue;
  
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper 
      onClick={onClick}
      className={onClick ? 'text-left hover:opacity-80 transition-opacity cursor-pointer' : ''}
    >
      <div className="text-[9px] font-medium text-white/70 uppercase tracking-[0.12em] mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`text-xl font-light tracking-tight ${highlight ? 'text-[#D4AF37]' : 'text-white'}`}>
          {value}
        </span>
        {showTrend && (
          <span className={`flex items-center gap-0.5 text-[10px] font-mono ${positive ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
            {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trend}
          </span>
        )}
      </div>
    </Wrapper>
  );
}

function PerformanceStageCard({ stage, index }) {
  const isLast = index === PERFORMANCE_STAGES.length - 1;
  
  return (
    <div className="relative">
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-lg p-4 hover:border-white/[0.15] transition-all duration-300">
        <div className="flex items-center justify-between mb-3">
          <span 
            className="px-2 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider"
            style={{ color: stage.color, background: `${stage.color}15` }}
          >
            {stage.label}
          </span>
          <span className="text-lg font-light text-white">{stage.count}</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-white/50">Capital</span>
          <span className="font-mono font-bold text-white/90">${(stage.capital / 1000).toFixed(0)}K</span>
        </div>
      </div>
      {!isLast && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10">
          <ChevronRight className="w-4 h-4 text-white/30" />
        </div>
      )}
    </div>
  );
}

function MetricRow({ label, value, trend, positive, highlight }) {
  // Hide trend indicators when value is $0 or zero
  const isZeroValue = value === "$0" || value === "$0.00" || value === "0" || value === "$0K" || value === "$0.00M";
  const showTrend = trend && !isZeroValue;
  
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/[0.02] last:border-0">
      <span className="text-[11px] text-white/50">{label}</span>
      <div className="flex items-center gap-3">
        <span className={`font-mono text-sm ${highlight ? 'text-[#D4AF37] font-medium' : 'text-white'}`}>
          {value}
        </span>
        {showTrend && (
          <span className={`flex items-center gap-0.5 text-[9px] font-mono ${positive ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
            {positive ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            {trend}
          </span>
        )}
      </div>
    </div>
  );
}

function StrategyRow({ strategy, count, profit, percentage }) {
  return (
    <div className="flex items-center gap-4 p-3 bg-white/[0.01] border border-white/[0.02] rounded-lg hover:bg-white/[0.02] transition-all duration-300">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-white font-medium">{strategy}</span>
          <span className="text-[10px] text-white/50">{count} deals</span>
        </div>
        <div className="h-1.5 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F2D06B] rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
      <div className="text-right min-w-[80px]">
        <span className={`text-sm font-mono font-medium ${profit >= 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
          {profit >= 0 ? '+' : ''}{(profit / 1000).toFixed(0)}K
        </span>
      </div>
    </div>
  );
}

function MonthCard({ month, data }) {
  return (
    <div className="bg-white/[0.01] border border-white/[0.06] p-4 rounded-lg hover:border-white/[0.15] transition-all duration-300">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-white font-medium">{month}</span>
        <span className="text-[9px] text-white/40">{data.count} deal{data.count !== 1 ? 's' : ''}</span>
      </div>
      <div className={`text-lg font-mono font-light ${data.profit >= 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
        {data.profit >= 0 ? '+' : ''}{(data.profit / 1000).toFixed(0)}K
      </div>
      <div className="text-[10px] text-white/40 mt-1">
        Rev: ${(data.revenue / 1000).toFixed(0)}K
      </div>
    </div>
  );
}

function StatusRow({ label, status }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-[11px] text-white/70">{label}</span>
      <div className="flex items-center gap-1.5">
        <div className={`w-1.5 h-1.5 rounded-full ${status === 'operational' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        <span className={`text-[9px] font-mono uppercase ${status === 'operational' ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'}`}>
          OK
        </span>
      </div>
    </div>
  );
}
